import { message } from 'antd';
import ClientsSelect from 'components/ClientsSelect';
import { FETCH_ERROR } from 'constants/alerts';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useDrawers } from 'hooks/useDrawers';
import { useFilters } from 'hooks/useFilters';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';
import { useQueryParam } from 'hooks/useQueryParam';
import { useTribe } from 'hooks/useTribe';
import { useEffect, useState } from 'react';
import { PaymentsService } from 'services/PaymentsService';
import { ActionsWrapper } from 'ui/ActionsWrapper';
import { thousandFormatter } from 'utils/forms/formatter';
import { PayedTaxesDrawer } from './PayedTaxesDrawer';
import { PayedTaxesTable } from './PayedTaxesTable';

const PAGE_TITLE = 'Оплата налогов (карточные)';

function PayedTaxes() {
  useDocumentTitle(PAGE_TITLE);
  const clientId = useQueryParam('client_id');
  const { setCount } = useHeaderChanger(PAGE_TITLE);
  const [loading, setLoading] = useState(false);
  const [cardPayments, setCardPayments] = useState([]);
  const tribeId = useTribe();
  const [summary, setSummary] = useState({
    total_amount: 0,
    total_fee: 0,
    total_payda_fee: 0,
    total_visa_amount: 0,
    total_acquiring_fee: 0,
    total_kgd_fee: 0,
  });
  const initialPaymentsInfo = {
    payments: null,
    statuses: null,
  };
  const [paymentsInfo, setPaymentsInfo] = useState(initialPaymentsInfo);
  const { filters, handleFilters, resetFilters } = useFilters({
    client_id: null,
    status: [],
    period: null,
    ordernum: null,
    f_created: null,
    t_created: null,
    send_date: null,
    f_sum: undefined,
    t_sum: undefined,
    visa: false,
    desc: null,
    pay_type: null,
  });
  const {
    page,
    perPage,
    totalRows,
    setPagination,
    setTotalRows,
    resetPagination,
  } = usePagination();
  const {
    state: { drawerVisible, activeRecord },
    openDrawer,
    closeDrawer,
  } = useDrawers();

  useEffect(() => {
    fetchCardPayments();
  }, [page, perPage, filters]);

  useEffect(() => {
    if (activeRecord) {
      fetchPaymentsInfo();
    }
  }, [activeRecord]);

  const fetchCardPayments = async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const {
        items,
        total,
        total_amount,
        total_fee,
        total_payda_fee,
        total_visa_amount,
        total_acquiring_fee,
        total_kgd_fee,
      } = await PaymentsService.getCardPayments(
        page,
        perPage,
        filters,
        tribeId
      );

      setCardPayments(items);
      setTotalRows(total);
      setCount(total);
      setSummary({
        total_amount,
        total_fee,
        total_payda_fee,
        total_visa_amount,
        total_acquiring_fee,
        total_kgd_fee,
      });
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentsInfo = async () => {
    setLoading(true);

    try {
      const { payments, statuses } =
        await PaymentsService.getCardPaymentsDetailV2(activeRecord?.id);

      setPaymentsInfo({
        payments,
        statuses,
      });
    } catch (err) {
      setPaymentsInfo(initialPaymentsInfo);
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleClientSelect = (id = '') => {
    resetFilters();
    handleFilters({ client_id: id });
    resetPagination();
  };

  return (
    <>
      <ActionsWrapper>
        <ClientsSelect
          pageTitle={PAGE_TITLE}
          clientId={clientId}
          onSelect={handleClientSelect}
          onClear={handleClientSelect}
        />
      </ActionsWrapper>

      <PayedTaxesTable
        allowFilter
        items={cardPayments}
        loading={loading}
        current={page}
        pageSize={perPage}
        totalRows={totalRows}
        filters={filters}
        summary={summary}
        onRowClick={openDrawer}
        onChange={setPagination}
        onFilter={(value) => {
          handleFilters(value);
          resetPagination();
        }}
      />

      {activeRecord && (
        <PayedTaxesDrawer
          title={`${thousandFormatter(activeRecord?.amount)} ₸ • ${
            activeRecord?.company_name
          }`}
          open={drawerVisible}
          onClose={closeDrawer}
          paymentsInfo={paymentsInfo}
          activeRecord={activeRecord}
        />
      )}
    </>
  );
}

export { PayedTaxes };
